import { omit } from 'lodash'
import {
  buildQueryParams,
  pageRedirect, getLocationPathname,
} from 'services/router'
import { extractQueryParams } from 'services/url'
import { graphql, readInlineData } from 'react-relay'
import { auth_getDefaultPath$key } from '__generated__/auth_getDefaultPath.graphql'
import { NextPageContext } from 'next'
import { auth_checkAuthForPage$key } from '__generated__/auth_checkAuthForPage.graphql'
import { auth_needsAccountSetup$key } from '__generated__/auth_needsAccountSetup.graphql'
import { auth_getRedirectPath$key } from '__generated__/auth_getRedirectPath.graphql'
import { Nullable } from 'types'
import { PayloadData } from 'relay-runtime'
import { isClient, isGuest, isMember } from './user'

/**
 * NOTE: prefer usage of the hook if possible
 * NOTE: keep in sync with getLegacyDefaultPath() if possible until that function is removed from the codebase
 */
export function getDefaultPath(userFragmentRef: Nullable<auth_getDefaultPath$key>) {
  const user = userFragmentRef ? readInlineData(graphql`
    fragment auth_getDefaultPath on User @inline {
      confirmedEmail
      passwordSetupRequired
      status

      activeContracts: freelancerContracts(
        first: 1,
        filters: { statuses: [active, paused] }
      ) {
        nodes {
          enableTimesheets
          allowExpensesAccess
          paymentsEnabled
          firm {
            slug
          }
        }
      }

      offers: freelancerContracts(
        first: 0,
        filters: {
          statuses: [offer_made]
        }
      ) {
        totalCount
      }

      interviews: freelancerJobApplications(
        first: 0,
        filters: {
          stage: interview
        }
      ) {
        totalCount
      }

      screening: freelancerJobApplications(
        first: 0,
        filters: {
          withJobScreening: true
        }
      ) {
        totalCount
      }

      applications: freelancerJobApplications(
        first: 0,
        filters: {
          statuses: [job_application_sent, job_application_draft],
        }
      ) {
        totalCount
      }

      ...user_isMember
      ...user_isClient
      ...user_isGuest
    }
  `, userFragmentRef) : null

  if (!isGuest(user) && user && !user.confirmedEmail) return '/confirm_email'
  if (!isGuest(user) && user?.passwordSetupRequired) return '/password_setup'
  if (isClient(user)) return '/dashboard'

  if (isMember(user)) {
    // If the user has an offer, always land on the offers page
    if ((user?.offers?.totalCount ?? 0) > 0) {
      return '/job_search/offers'
    }

    // Otherwise if the user has an active contract, land on the appropriate my_work page
    if ((user?.activeContracts?.nodes?.length ?? 0) > 0) {
      let tab = 'contract'
      const contract = user?.activeContracts?.nodes?.[0]

      if (contract?.firm?.slug) {
        if (contract.paymentsEnabled) {
          tab = 'contract'
        } else if (contract.enableTimesheets) {
          tab = 'work_reports'
        } else if (contract.allowExpensesAccess) {
          tab = 'expenses'
        } else {
          tab = 'permissions'
        }

        return `/my_work/${tab}?company=${contract?.firm?.slug}`
      }
    }

    // Otherwise if we get to this point, then force pending users to create their profile
    // IMPORTANT: we only want to drive the user to complete their profile if they are looking for a job.
    // NOTE: do not force pending members to create the profile if they are working as clients too
    if (user?.status === 'pending') return '/profile'

    if ((user?.interviews?.totalCount ?? 0) > 0) {
      return '/job_search/interviews'
    }

    if ((user?.screening?.totalCount ?? 0) > 0) {
      return '/job_search/screening'
    }

    if ((user?.applications?.totalCount ?? 0) > 0) {
      return '/job_search/applied'
    }

    return '/job_search/opportunities'
  }

  return '/login'
}

export function getRedirectUrl(query: Record<string, string>) {
  if (!query?.url) return null
  const urlParams = extractQueryParams(query.url)
  const params = buildQueryParams({ ...omit(query, 'url'), ...urlParams })
  return `${getLocationPathname(query.url)}${params}`
}

/**
 * Get where the user should be redirected to, if they can't access the current URL, depending on what the
 * current URL is.
 *
 * NOTE: keep in sync with getLegacyRedirectPath if possible, until that function is removed
 */
export function getRedirectPath(userFragmentRef: Nullable<auth_getRedirectPath$key>, urlAsPath: Nullable<string>) {
  const user = userFragmentRef ? readInlineData(graphql`
    fragment auth_getRedirectPath on User @inline {
      status
      unconfirmedEmail
      confirmedEmail
      integrationName

      ...auth_getDefaultPath
      ...user_isGuest
      ...user_isMember
      ...user_isClient
    }
  `, userFragmentRef) : null

  const query = extractQueryParams(urlAsPath)
  const currentUrl = urlAsPath ? getLocationPathname(urlAsPath) : null
  const defaultPath = getDefaultPath(user)

  if (user && !isGuest(user)) {
    if (isMember(user) && !isClient(user) && user.status !== 'pending' && query?.job) {
      // This is for users signing up or logging in for a job, we send them straight to the apply dialog
      return `/job/${query.job}?applying=true`
    }

    if (isClient(user) && currentUrl === '/flexmanage') {
      // This is for users signing up for "FlexManage", we send them to the team invitation page
      // TODO: this is awful, clean it up
      return '/invite'
    }

    if ((currentUrl === '/login' || currentUrl === '/signup') && query?.url) {
      // If the user it at /login?url=... but they are logged in and have a "url" param, we send them there
      return getRedirectUrl(query)
    }

    if ((user.confirmedEmail && !user.unconfirmedEmail) && currentUrl === '/confirm_email' && query?.url) {
      // If the user it at /confirm_email?url=... but they are fully confirmed and have a "url" param, we send them there
      return getRedirectUrl(query)
    }

    if (isMember(user) && !isClient(user) && user.status === 'pending' && user.integrationName) {
      // This user was imported from greenhouse, they should go complete their profile
      return '/profile'
    }
  }

  // At this point no special case is happening so we send the user to the default path
  const path = defaultPath

  if ([
    '/login',
    '/confirm_email',
    '/password_setup',
  ].includes(path)) {
    // If we are sending the user to login or confirm their email
    // we add a ?url= param so we can keep track of where they are coming from
    const search = buildQueryParams({
      url: currentUrl,
      ...query,
    })

    return `${path}${search}`
  }

  // Otherwise we just redirect them to the default page for their type
  // while keeping the query params.
  const newQuery = buildQueryParams(query)
  return `${path}${newQuery}`
}

/**
 * Check if the user is allowed access to the page, if not redirect them automatically to their homepage or
 * to a custom URL.
 *
 * @param context Next.js page context
 * @param userFragmentRef fragment ref for the current user
 * @param allowed if the user can access the page
 * @param customRedirectTo where to redirect the user to (optional). Otherwise their homepage will be used.
 */
export function checkAuthForPage(
  context: NextPageContext,
  userFragmentRef: Nullable<auth_checkAuthForPage$key>,
  allowed: boolean,
  customRedirectTo: string | null = null,
) {
  const user = userFragmentRef ? readInlineData(graphql`
    fragment auth_checkAuthForPage on User @inline {
      ...auth_getRedirectPath
    }
  `, userFragmentRef) : null
  const defaultRedirectTo = getRedirectPath(user, context.asPath)
  const redirectTo = customRedirectTo || defaultRedirectTo

  if (!allowed && redirectTo) {
    console.log('Server Side Auth: not allowed, redirecting to', redirectTo)
    // TODO: implement redirect loop check
    pageRedirect(context.res, redirectTo, 'private, no-cache')

    return redirectTo
  }
  console.log('Server Side Auth: allowed, not redirecting')

  return null
}

/**
 * Return a serverSidePostQuery function to be passed to withRelay/withPreloadedQuery that will check if the user
 * is allowed to access the page, or automatically redirect them away, depending on the authorizer function passed.
 *
 * TODO: fix types
 *
 * @param authorizer the function to use to check if the user is allowed
 * @param customRedirectTo optionally pass a specific URL the user will be redirected to
 * if the user is not allowed access. Otherwise their default homepage will be used.
 */
export function serverSidePostQueryAuth<T extends { response: any }>(
  authorizer: (queryResult: { data: T['response'] } | undefined, context: NextPageContext) => boolean | Promise<boolean>,
  customRedirectTo: string | null = null,
) {
  return async function (queryResult: { data: T['response'] } | undefined, context: NextPageContext) {
    try {
      const user = queryResult?.data?.currentUser || null
      // There is a known issue with queryResult where it contains the raw response, and does not contain data about
      // which fragments have been expanded on it, even though the fragment has been expanded.
      // This causes readInlineData to crash when used on it, unless the value passed is null
      // This means if currentUser is defined, the authorizer call will most likely crash.
      // TODO: fix this issue by implementing our own readInlineData or not using it in the isAllowed functions
      const allowed = await authorizer(queryResult, context)
      return checkAuthForPage(context, user, allowed, customRedirectTo)
    } catch (error) {
      // this error is suppressed because we know the cause (see above TODO)
      // TODO: fix the root cause then re-enable error reporting
      // trackError(error)
    }

    return null
    // TODO: remove the 'as' cast and fix resulting type errors
  } as (queryResult: PayloadData | undefined, context: NextPageContext) => Promise<string | null>
}

/**
 * Returns if a user needs to complete their account setup, for example by confirming the email.
 */
export function needsAccountSetup(userFragmentRef: auth_needsAccountSetup$key | null, allowUnconfirmedEmail: boolean = false) {
  const user = readInlineData(graphql`
    fragment auth_needsAccountSetup on User @inline {
      guest
      confirmedEmail
      passwordSetupRequired
    }
  `, userFragmentRef)

  return Boolean(user && !user?.guest && ((!user?.confirmedEmail && !allowUnconfirmedEmail) || user?.passwordSetupRequired))
}

/**
 * Use this function in serverSidePostQuery at the page level to handle setting 404 status code if the content
 * for the page has not been found.
 *
 * @param found whether the content has been found or not
 * @param ctx page context, you can get this from the second parameter to serverSidePostQuery
 */
export function handleNotFound(found: boolean, ctx: NextPageContext) {
  if (!found && ctx?.res) {
    console.log('Server Side: data not found, writing head 404')
    ctx.res.statusCode = 404
  }
}
